import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core'
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import type { Entity, Group } from '@app/shared/models/common-models'
import type { PropertyChangeArgs } from '@app/shared/models/entity-update.model'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'

@UntilDestroy()
@Component({
  selector: 'app-entity-editor',
  templateUrl: './entity-editor.component.html',
  styleUrls: ['./entity-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EntityEditorComponent implements OnInit, OnChanges, AfterViewInit {
  formGroup: UntypedFormGroup

  @Input() entity: Entity

  @Input() groupFilter: (group: Group) => boolean

  @Input() resetForm: boolean

  @Input() hasExpansionGroups: boolean

  @Input() expandedGroups: string[]

  @Output() propertyUpdate = new EventEmitter<PropertyChangeArgs>()

  @Output() formValidityUpdate = new EventEmitter<{ valid: boolean; dirty: boolean }>()

  constructor(
    formBuilder: UntypedFormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    this.formGroup = formBuilder.group({})
  }

  ngOnInit(): void {
    this.formGroup.statusChanges.pipe(untilDestroyed(this)).subscribe({
      next: () => {
        this.formValidityUpdate.emit({ valid: this.formGroup.valid, dirty: this.formGroup.dirty })
      },
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.formGroup &&
      changes.resetForm &&
      changes.resetForm.currentValue &&
      changes.resetForm.currentValue !== changes.resetForm.previousValue
    ) {
      this.formGroup.markAsPristine()
    }
  }

  ngAfterViewInit(): void {
    Object.keys(this.formGroup.controls)
      .map(key => {
        return this.formGroup.get(key)
      })
      .forEach(formControl => {
        formControl.updateValueAndValidity({ onlySelf: true, emitEvent: false })
        formControl.markAsTouched()
      })

    this.formGroup.updateValueAndValidity({ onlySelf: false })
    this.changeDetectorRef.detectChanges()
  }

  filterGroups = (group: Group): boolean => {
    if (this.groupFilter) {
      return this.groupFilter(group) && group.IsVisible
    }

    return group.IsVisible
  }

  onPropertyChange(args: PropertyChangeArgs): void {
    const controls = Object.keys(this.formGroup.controls).map(key => this.formGroup.get(key))

    controls.forEach(formControl => {
      formControl.updateValueAndValidity({ onlySelf: true, emitEvent: false })
      formControl.markAsTouched()
    })

    this.formGroup.updateValueAndValidity({ onlySelf: false, emitEvent: false })

    this.propertyUpdate.emit(args)
  }

  groupTrackBy = (index: number, group: Group): string => {
    return group.Key
  }

  isGroupExpanded(group: Group): boolean {
    return this.expandedGroups && this.expandedGroups.includes(group.Key)
  }
}
